import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import moment from "moment";
import AdminHeader from "./Header";

// const BASE = "http://localhost/business_community/";
const BASE = "https://qnexus.in/community/";
const FALLBACK_AVATAR = "https://ui-avatars.com/api/?background=1e3a8a&color=fff&name=";
const POLL_INTERVAL = 5000;

const AdminChatPage = () => {
    const { state } = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const toUser = state?.business_user_id;
    const other  = state?.other_business;

    const [message, setMessage]       = useState("");
    const [messages, setMessages]     = useState([]);
    const [role, setRole]             = useState("admin");
    const [activeMenu, setActiveMenu] = useState(null);
    const [showClearConfirm, setShowClearConfirm] = useState(false);

    const bottomRef = useRef();
    const pollRef   = useRef();
    const msgBoxRef = useRef();

    /* ── Detect role ── */
    useEffect(() => {
        try {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            setRole(user?.role || "admin");
        } catch { setRole("admin"); }
    }, []);

    /* ── Close menu on outside click ── */
    useEffect(() => {
        const close = () => setActiveMenu(null);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, []);

    /* ── Load messages ── */
    const loadMessages = useCallback(async (showLoader = false) => {
        if (!toUser) return;
        if (showLoader) dispatch(loadingTrue());
        try {
            const r = await Handler({
                method: "get",
                url: `/admin_chat/message.php?business_user_id=${toUser}`
            });
            if (r?.data?.list) setMessages(r.data.list);
        } catch (e) { Handler(e); }
        if (showLoader) dispatch(loadingFalse());
    }, [toUser, dispatch]);

    /* ── Initial load + polling ── */
    useEffect(() => {
        if (!toUser) return;
        loadMessages(true);
        Handler({ method: "post", url: "/admin_chat/mark_read.php", data: { business_user_id: toUser } }).catch(() => {});
        pollRef.current = setInterval(() => loadMessages(false), POLL_INTERVAL);
        return () => clearInterval(pollRef.current);
    }, [toUser, loadMessages]);

    /* ── Scroll to bottom — only the message box, never the page ── */
    useEffect(() => {
        if (msgBoxRef.current) {
            msgBoxRef.current.scrollTop = msgBoxRef.current.scrollHeight;
        }
    }, [messages]);

    /* ── Send ── */
    const sendMessage = async () => {
        if (!message.trim() || !toUser) return;
        const text = message.trim();
        setMessage("");
        const temp = { id: `temp-${Date.now()}`, text, is_me: true, created_at: new Date().toISOString(), temp: true };
        setMessages(prev => [...prev, temp]);
        try {
            await Handler({ method: "post", url: "/admin_chat/send.php", data: { business_user_id: toUser, message: text, message_type: "TEXT" } });
            await loadMessages(false);
        } catch (e) {
            Handler(e);
            setMessages(prev => prev.filter(m => m.id !== temp.id));
            setMessage(text);
        }
    };

    /* ── Delete for me ── */
    const deleteMessage = async (messageId) => {
        setActiveMenu(null);
        try {
            await Handler({ method: "post", url: "/admin_chat/delete.php", data: { action: "delete_message", message_id: messageId } });
            setMessages(prev => prev.filter(m => m.id !== messageId));
        } catch (e) { Handler(e); }
    };

    /* ── Delete for everyone (business only) ── */
    const deleteForEveryone = async (messageId) => {
        setActiveMenu(null);
        try {
            await Handler({ method: "post", url: "/admin_chat/delete.php", data: { action: "delete_for_everyone", message_id: messageId } });
            setMessages(prev => prev.filter(m => m.id !== messageId));
        } catch (e) { Handler(e); }
    };

    /* ── Clear entire chat ── */
    const deleteChat = async () => {
        setShowClearConfirm(false);
        dispatch(loadingTrue());
        try {
            await Handler({ method: "post", url: "/admin_chat/delete.php", data: { action: "delete_chat", business_user_id: toUser } });
            navigate(-1);
        } catch (e) { Handler(e); }
        dispatch(loadingFalse());
    };

    /* ── Guard ── */
    if (!toUser) return (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
            Chat not found. <button onClick={() => navigate(-1)}>Go back</button>
        </div>
    );

    const businessName = other?.business_name || "Business";
    const logo = other?.logo ? BASE + other.logo : `${FALLBACK_AVATAR}${encodeURIComponent(businessName)}`;

    return (
        <React.Fragment>
            <AdminHeader />

            <div style={{ background: "#f0f4f8", minHeight: "100vh", padding: "20px 0" }}>
                <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 16px" }}>

                    <h3 style={{ fontWeight: 700, color: "#1a365d", marginBottom: 16 }}>Chat Now</h3>

                    {/* ── Chat Box ── */}
                    <div style={{
                        background: "#fff",
                        borderRadius: 16,
                        overflow: "hidden",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                        display: "flex",
                        flexDirection: "column",
                        height: "75vh"
                    }}>

                        {/* ── Top Bar ── */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px 16px",
                            background: "#1e3a8a",
                            color: "#fff"
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <img
                                    src={logo}
                                    alt={businessName}
                                    onError={e => { e.target.src = `${FALLBACK_AVATAR}${encodeURIComponent(businessName)}`; }}
                                    style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,0.4)" }}
                                />
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: "0.95rem", textTransform: "capitalize" }}>{businessName}</div>
                                    <div style={{ fontSize: "0.72rem", opacity: 0.75 }}>Business Account</div>
                                </div>
                            </div>

                            <div style={{ display: "flex", gap: 8 }}>
                                <button
                                    onClick={() => setShowClearConfirm(true)}
                                    style={{
                                        background: "rgba(233, 239, 68, 0.15)",
                                        border: "1px solid rgba(239, 213, 68, 0.5)",
                                        color: "#fdfdfd",
                                        borderRadius: 8,
                                        padding: "6px 12px",
                                        fontSize: "0.8rem",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6
                                    }}
                                >
                                    <i className="fa-solid fa-trash-can" /> Clear Chat
                                </button>
                                <button
                                    onClick={() => navigate(-1)}
                                    style={{
                                        background: "#f59e0b",
                                        border: "none",
                                        color: "#fff",
                                        borderRadius: 8,
                                        padding: "6px 14px",
                                        fontSize: "0.8rem",
                                        fontWeight: 600,
                                        cursor: "pointer"
                                    }}
                                >
                                    Back
                                </button>
                            </div>
                        </div>

                        {/* ── Messages ── */}
                        <div
                            ref={msgBoxRef}
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                padding: "16px 14px",
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                                background: "#f8fafc"
                            }}
                        >
                            {messages.length === 0 && (
                                <div style={{ textAlign: "center", color: "#94a3b8", margin: "auto", padding: "40px 0" }}>
                                    <i className="fa-regular fa-comments" style={{ fontSize: 36, opacity: 0.3, display: "block", marginBottom: 10 }} />
                                    No messages yet. Say hello!
                                </div>
                            )}

                            {messages.map((m, i) => {
                                const isMe = m.is_me;
                                return (
                                    <div
                                        key={m.id ?? i}
                                        style={{
                                            display: "flex",
                                            justifyContent: isMe ? "flex-end" : "flex-start",
                                            alignItems: "flex-end",
                                            gap: 6
                                        }}
                                    >
                                        {!isMe && (
                                            <img
                                                src={logo}
                                                alt=""
                                                onError={e => { e.target.src = `${FALLBACK_AVATAR}${encodeURIComponent(businessName)}`; }}
                                                style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                                            />
                                        )}

                                        <div style={{ maxWidth: "65%", position: "relative" }}>
                                            <div style={{
                                                background: isMe ? "#1e3a8a" : "#fff",
                                                color: isMe ? "#fff" : "#1a365d",
                                                padding: "9px 13px",
                                                borderRadius: isMe ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                                                fontSize: "0.88rem",
                                                lineHeight: 1.45,
                                                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                                                opacity: m.temp ? 0.6 : 1,
                                                wordBreak: "break-word"
                                            }}>
                                                {m.text || m.message}
                                            </div>

                                            <div style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: isMe ? "flex-end" : "flex-start",
                                                gap: 4,
                                                marginTop: 3
                                            }}>
                                                <span style={{ fontSize: "0.68rem", color: "#94a3b8" }}>
                                                    {moment(m.created_at).format("hh:mm A")}
                                                </span>

                                                {isMe && !m.temp && (
                                                    <div style={{ position: "relative" }}>
                                                        <button
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                setActiveMenu(activeMenu === m.id ? null : m.id);
                                                            }}
                                                            style={{
                                                                background: "none",
                                                                border: "none",
                                                                color: "#94a3b8",
                                                                cursor: "pointer",
                                                                padding: "0 3px",
                                                                fontSize: 10,
                                                                lineHeight: 1
                                                            }}
                                                            title="Options"
                                                        >
                                                            <i className="fa-solid fa-chevron-down" />
                                                        </button>

                                                        {activeMenu === m.id && (
                                                            <div
                                                                onClick={e => e.stopPropagation()}
                                                                style={{
                                                                    position: "absolute",
                                                                    bottom: 20,
                                                                    right: 0,
                                                                    background: "#fff",
                                                                    borderRadius: 10,
                                                                    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                                                                    zIndex: 100,
                                                                    minWidth: 175,
                                                                    overflow: "hidden"
                                                                }}
                                                            >
                                                                <button
                                                                    onClick={() => deleteMessage(m.id)}
                                                                    style={dropItemStyle}
                                                                >
                                                                    <i className="fa-solid fa-eye-slash me-2" />
                                                                    Delete for me
                                                                </button>
                                                                {role === "business" && (
                                                                    <button
                                                                        onClick={() => deleteForEveryone(m.id)}
                                                                        style={{ ...dropItemStyle, color: "#ef4444" }}
                                                                    >
                                                                        <i className="fa-solid fa-trash me-2" />
                                                                        Delete for everyone
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={bottomRef} />
                        </div>

                        {/* ── Input Bar ── */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "10px 14px",
                            borderTop: "1px solid #e2e8f0",
                            background: "#fff"
                        }}>
                            <input
                                value={message}
                                onChange={e => setMessage(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && sendMessage()}
                                placeholder="Write a message..."
                                style={{
                                    flex: 1,
                                    border: "1.5px solid #e2e8f0",
                                    borderRadius: 22,
                                    padding: "10px 16px",
                                    fontSize: "0.88rem",
                                    outline: "none",
                                    background: "#f8fafc",
                                    color: "#1a365d",
                                    fontFamily: "inherit"
                                }}
                                onFocus={e => { e.target.style.borderColor = "#1e3a8a"; e.target.style.background = "#fff"; }}
                                onBlur={e => { e.target.style.borderColor = "#e2e8f0"; e.target.style.background = "#f8fafc"; }}
                            />
                            <button
                                onClick={sendMessage}
                                disabled={!message.trim()}
                                style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: "50%",
                                    border: "none",
                                    background: message.trim() ? "#1e3a8a" : "#cbd5e0",
                                    color: "#fff",
                                    cursor: message.trim() ? "pointer" : "not-allowed",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "0.9rem",
                                    flexShrink: 0,
                                    transition: "background 0.2s"
                                }}
                            >
                                <i className="fa-solid fa-paper-plane" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Confirm Clear Dialog ── */}
            {showClearConfirm && (
                <div
                    onClick={() => setShowClearConfirm(false)}
                    style={{
                        position: "fixed", inset: 0,
                        background: "rgba(0,0,0,0.4)",
                        zIndex: 999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: "#fff",
                            borderRadius: 14,
                            padding: "24px",
                            maxWidth: 340,
                            width: "90%",
                            boxShadow: "0 8px 32px rgba(0,0,0,0.18)"
                        }}
                    >
                        <h6 style={{ fontWeight: 700, marginBottom: 6, color: "#1a365d" }}>Clear Conversation?</h6>
                        <p style={{ color: "#64748b", fontSize: "0.85rem", marginBottom: 18 }}>
                            This removes the entire chat from <strong>your view only</strong>. The other party will still see all messages.
                        </p>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <button
                                onClick={() => setShowClearConfirm(false)}
                                style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", fontSize: "0.85rem" }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={deleteChat}
                                style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem" }}
                            >
                                Yes, Clear
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </React.Fragment>
    );
};

const dropItemStyle = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "10px 14px",
    fontSize: "0.83rem",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#334155",
    whiteSpace: "nowrap"
};

export default AdminChatPage;